// src/components/app/project/Hero2.jsx
"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import "./project.css";

import Img1 from "../../assets/img/project/Genome.png";
import Img2 from "../../assets/img/project/Effet_Mer.png";
import Img3 from "../../assets/img/project/Maison_Sambin.png";
import Img4 from "../../assets/img/project/Los_Pistoleros.png";
import Img5 from "../../assets/img/project/Maison_Marline.png";
import Img6 from "../../assets/img/project/People.png";
import Img7 from "../../assets/img/project/FannyArt.png";
import Img8 from "../../assets/img/project/JppCompta.png";

import Img1_mobile from "../../assets/img/project/Genome_mobile.png";
import Img2_mobile from "../../assets/img/project/Effet_Mer_mobile.png";
import Img3_mobile from "../../assets/img/project/Maison_Sambin_mobile.png";
import Img4_mobile from "../../assets/img/project/Los_Pistoleros_mobile.png";
import Img5_mobile from "../../assets/img/project/Maison_Marline_mobile.png";
import Img6_mobile from "../../assets/img/project/People_mobile.png";
import Img7_mobile from "../../assets/img/project/FannyArt_mobile.png";
import Img8_mobile from "../../assets/img/project/JppCompta_mobile.png";

export default function StickyBandCards() {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const EXTRA_UNITS = 1;
  const prevActiveRef = useRef("all");

  const [active, setActive] = useState("all");
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  let rafId = null;
  const setCursorVars = (el, x, y) => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      el.style.setProperty("--mx", `${x}px`);
      el.style.setProperty("--my", `${y}px`);
    });
  };
  const onPointerMove = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    setCursorVars(el, e.clientX - r.left, e.clientY - r.top);
  };
  const onPointerEnter = (e) => {
    const el = e.currentTarget;
    el.dataset.cursor = "show";
    const r = el.getBoundingClientRect();
    setCursorVars(el, e.clientX - r.left, e.clientY - r.top);
  };
  const onPointerLeave = (e) => {
    e.currentTarget.dataset.cursor = "hide";
  };

  const Fiches = useMemo(
    () => [
      {
        title: "EFFET MER",
        texte: "Web design - Design graphique",
        img: Img2,
        mobileImg: Img2_mobile,
        index: 2,
        href: "/effet_mer#hero",
        categories: ["web", "graphic"],
      },
      {
        title: "GENOME",
        texte: "Design graphique",
        img: Img1,
        mobileImg: Img1_mobile,
        index: 1,
        href: "/genome#hero",
        categories: ["graphic"],
      },
      {
        title: "MAISON SAMBIN",
        texte: "Web design",
        img: Img3,
        mobileImg: Img3_mobile,
        index: 3,
        href: "/maison_sambin#hero",
        categories: ["web"],
      },
      {
        title: "LOS PISTOLEROS",
        texte: "Web design",
        img: Img4,
        mobileImg: Img4_mobile,
        index: 4,
        href: "/los_pistoleros#hero",
        categories: ["web"],
      },
      {
        title: "JPP COMPTA",
        texte: "Web design - Design graphique",
        img: Img8,
        mobileImg: Img8_mobile,
        index: 8,
        href: "/jpp_compta#hero",
        categories: ["web", "graphic"],
      },
      {
        title: "PEOPLE",
        texte: "Web design - Design graphique - Social média",
        img: Img6,
        mobileImg: Img6_mobile,
        index: 6,
        href: "/people#hero",
        categories: ["web", "social", "graphic"],
      },
      {
        title: "GAUBERTI",
        texte: "Social média",
        img: Img7,
        mobileImg: Img7_mobile,
        index: 7,
        href: "/galerie-fanny#hero",
        categories: ["social"],
      },
      {
        title: "MAISON MARLINE",
        texte: "Design graphique",
        img: Img5,
        mobileImg: Img5_mobile,
        index: 5,
        href: "/maison_marline#hero",
        categories: ["graphic"],
      },
    ],
    []
  );

  const list = useMemo(() => {
    if (active === "all") return Fiches;
    return Fiches.map((f) => ({
      ...f,
      hidden: !(f.categories || []).includes(active),
    }));
  }, [active, Fiches]);

  // Animation fade in/fade out lors du changement de filtres
  useLayoutEffect(() => {
    // Ne pas animer si le filtre n'a pas changé
    if (prevActiveRef.current === active) return;

    const cards = gsap.utils.toArray(".Card");
    const cardsToHide = [];
    const cardsToShow = [];
    const cardsCurrentlyVisible = [];

    // Séparer les cartes en catégories
    cards.forEach((card) => {
      const inner = card.querySelector(".Inner");
      const text = card.querySelector(".Text");
      if (!inner || !text) return;

      const isHidden = card.classList.contains("isHidden");
      const wasHidden = window.getComputedStyle(card).display === "none";

      if (isHidden && !wasHidden) {
        // Cartes qui vont être cachées
        cardsToHide.push({ card, inner, text });
      } else if (!isHidden && wasHidden) {
        // Cartes qui vont être affichées
        cardsToShow.push({ card, inner, text });
      } else if (!isHidden && !wasHidden) {
        // Cartes actuellement visibles qui restent visibles
        cardsCurrentlyVisible.push({ card, inner, text });
      }
    });

    // Créer une timeline pour orchestrer les animations
    const tl = gsap.timeline();

    // Étape 1: Fade out de toutes les cartes actuellement visibles
    const allVisibleCards = [...cardsToHide, ...cardsCurrentlyVisible];
    if (allVisibleCards.length > 0) {
      const fadeOutTargets = [];
      allVisibleCards.forEach(({ card, inner, text }) => {
        fadeOutTargets.push(card, inner, text);
      });

      tl.to(fadeOutTargets, {
        autoAlpha: 0,
        duration: 0.3,
        ease: "power2.inOut",
        onComplete: () => {
          // Cacher les cartes qui doivent être cachées
          cardsToHide.forEach(({ card }) => {
            card.style.display = "none";
          });
        },
      });
    }

    // Étape 2: Fade in des nouvelles cartes filtrées
    if (cardsToShow.length > 0) {
      const fadeInTargets = [];
      cardsToShow.forEach(({ card, inner, text }) => {
        card.style.display = "grid";
        gsap.set([card, inner, text], { autoAlpha: 0 });
        fadeInTargets.push(card, inner, text);
      });

      // Si des cartes restent visibles, les inclure dans le fade in
      if (cardsCurrentlyVisible.length > 0) {
        cardsCurrentlyVisible.forEach(({ card, inner, text }) => {
          fadeInTargets.push(card, inner, text);
        });
      }

      tl.to(
        fadeInTargets,
        {
          autoAlpha: 1,
          duration: 0.4,
          ease: "power2.inOut",
        },
        "-=0.1"
      ); // Commencer légèrement avant la fin du fade out
    } else if (cardsCurrentlyVisible.length > 0) {
      // Si aucune nouvelle carte mais des cartes restent visibles
      const fadeInTargets = [];
      cardsCurrentlyVisible.forEach(({ card, inner, text }) => {
        fadeInTargets.push(card, inner, text);
      });

      tl.to(
        fadeInTargets,
        {
          autoAlpha: 1,
          duration: 0.4,
          ease: "power2.inOut",
        },
        "-=0.1"
      );
    }

    prevActiveRef.current = active;
  }, [active]);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      ScrollTrigger.getAll().forEach((st) => {
        if (sectionRef.current && st.trigger === sectionRef.current) st.kill();
      });

      const cards = gsap.utils.toArray(".Card:not(.isHidden)");
      if (!cards.length) return;

      // Utiliser des valeurs absolues au lieu de yPercent pour Firefox
      const cardHeight = window.innerHeight;
      gsap.set(cards, {
        y: cardHeight,
        autoAlpha: 0,
        scale: 0.96,
        transformOrigin: "50% 80%",
        willChange: "transform, opacity",
        zIndex: 0,
        force3D: true, // Force l'accélération matérielle
      });
      gsap.set(cards[0], { y: 0, autoAlpha: 1, scale: 1, zIndex: 100 });

      const stepsCount = Math.max(cards.length - 1, 1);
      const isTouch = matchMedia("(pointer: coarse)").matches;

      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: `+=${(stepsCount + EXTRA_UNITS) * 100}%`,
          pin: true,
          scrub: 1,
          snap: isTouch
            ? false
            : {
                snapTo: "labelsDirectional",
                duration: { min: 0.3, max: 0.5 },
                ease: "power2.inOut",
              },
        },
      });

      tl.addLabel("card-0", 0);

      cards.forEach((card, i) => {
        if (i === 0) return;
        const prev = cards[i - 1];
        const at = i;

        tl.addLabel(`card-${i}`, at);
        tl.set(card, { zIndex: 100 + i }, at);
        tl.to(card, { y: 0, autoAlpha: 1, scale: 1, duration: 0.35 }, at);
        tl.to(prev, { scale: 0.92, duration: 0.35, ease: "power1.out" }, at);
        tl.to(prev, { autoAlpha: 0, duration: 0.2 }, at + 0.3);
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [active]);

  // Animation d'apparition de la page
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.3 });

      // Animation des boutons de filtre
      tl.fromTo(
        ".FilterBtn",
        {
          y: -30,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1, // Force l'opacité à 1 et visibility à visible
          duration: 0.8,
          stagger: 0.1,
          ease: "power3.out",
          clearProps: "all", // Nettoie les styles après l'animation pour éviter les conflits
        }
      );

      // Animation d'apparition de la première carte (pour accompagner)
      // On cible le contenu car le conteneur Card est géré par ScrollTrigger
      const firstCardContent = document.querySelector(
        ".Card:not(.isHidden) .Inner"
      );
      if (firstCardContent) {
        tl.from(
          firstCardContent,
          {
            y: 50,
            opacity: 0,
            duration: 1,
            ease: "power3.out",
          },
          "<0.1"
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef}>
      <nav className="CardsFilters" aria-label="Filtres">
        <button
          className={`FilterBtn ${active === "all" ? "isActive" : ""}`}
          onClick={() => setActive("all")}
          type="button"
        >
          Tous
        </button>
        <button
          className={`FilterBtn ${active === "social" ? "isActive" : ""}`}
          onClick={() => setActive("social")}
          type="button"
        >
          Social média
        </button>
        <button
          className={`FilterBtn ${active === "web" ? "isActive" : ""}`}
          onClick={() => setActive("web")}
          type="button"
        >
          Web design
        </button>
        <button
          className={`FilterBtn ${active === "graphic" ? "isActive" : ""}`}
          onClick={() => setActive("graphic")}
          type="button"
        >
          Design graphique
        </button>
      </nav>

      <section ref={sectionRef} className="Section">
        {list.map((fiche) => {
          const hidden = !!fiche.hidden;
          const imgSrc =
            isMobile && fiche.mobileImg ? fiche.mobileImg : fiche.img;
          const CardContent = (
            <>
              <article className="Text">
                <h3>{fiche.title}</h3>
                <p>{fiche.texte}</p>
              </article>

              <div className="ImageWrap">
                <img
                  src={imgSrc}
                  alt={fiche.title}
                  className="Img"
                  loading={fiche.index === 1 ? "eager" : "lazy"}
                />
              </div>

              <div className="CursorCircle">
                <span>Click me!</span>
              </div>
            </>
          );

          return (
            <div
              key={fiche.index}
              className={`Card ${hidden ? "isHidden" : ""}`}
            >
              <Link
                className="Inner"
                to={fiche.href}
                onPointerMove={onPointerMove}
                onPointerEnter={onPointerEnter}
                onPointerLeave={onPointerLeave}
              >
                {CardContent}
              </Link>
            </div>
          );
        })}
      </section>
    </div>
  );
}
